import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDialog } from '../../hooks/use-app-dialog'
import {
  ItemDetails,
  Nonprofit,
  NonprofitCategory,
} from '../../models'
import { ApiError } from '../../services/api/api-error-data'
import { listedItemsApi } from '../../services/api/listed-items'
import { nonprofitsApi } from '../../services/api/nonprofits'
import { AppDialogVariants } from '../../store/app-dialog/types'
import { trackException } from '../../store/app-insights/reducer'
import { setDeliveryType } from '../../store/checkout-page/reducer'
import { selectDeliveryType } from '../../store/checkout-page/selectors'
import {
  selectCheckedAuth,
  selectIsAuthenticated,
  selectUser,
} from '../../store/auth/selectors'

type UseListedItemDetailsPage = {
  detailProps?: ItemDetails
  loading: boolean
  getNonprofitInfo: (nonprofitName: string) => void
  deliveryType: string
  setInStoreDeliveryType: (value: string) => void
  nonprofitProps?: Nonprofit
}

export const useListedItemDetailsPage = (): UseListedItemDetailsPage => {
  const navigate = useNavigate()
  const { itemId } = useParams()
  const dispatch = useDispatch()
  const [detailProps, setDetailProps] = useState<ItemDetails>()
  const { t } = useTranslation()
  const [loading, setLoading] = useState<boolean>(true)
  const { setAppDialog } = useAppDialog()
  const deliveryType = useSelector(selectDeliveryType)
  const [nonprofitProps, setNonprofitProps] = useState<Nonprofit>()
  const checkedAuth = useSelector(selectCheckedAuth)
  const authenticated = useSelector(selectIsAuthenticated)
  const user = useSelector(selectUser)

  useEffect(() => {
    if (!itemId || !checkedAuth) {
      return
    }

    setLoading(true)
    listedItemsApi
      .readDetails(`${itemId}`)
      .then((data) => {
        setLoading(false)
        setDetailProps(data)
      })
      .catch((reason) => {
        setAppDialog({
          isShow: true,
          title: 'Error',
          message: (reason as ApiError).getErrorMessage(t),
          variant: AppDialogVariants.error,
          actions: null,
          closeHandler: () => navigate(-1),
        })
        setLoading(false)
        dispatch(trackException({ e: reason, source: 'getListedItemDetails' }))
      })
  }, [itemId, checkedAuth, authenticated, user.userId])

  const setInStoreDeliveryType = (value: string) => {
    dispatch(setDeliveryType(value))
  }

  const getNonprofitInfo = useCallback((nonprofitName: string) => {
    nonprofitsApi
      .lookupData({
        categoryId: '',
        searchName: nonprofitName,
        pageIndex: '',
        pageSize: '',
      })
      .then(async (data) => {
        const row = data?.[0] as Nonprofit | undefined
        if (!row) {
          setNonprofitProps(undefined)
          return
        }
        let nonprofitCategoryName: string | undefined
        try {
          const categories: NonprofitCategory[] =
            await nonprofitsApi.categories()
          nonprofitCategoryName = categories.find(
            (c) => c.nonprofitCategoryId === row.nonprofitCategoryId
          )?.name
        } catch {
          /* breadcrumbs still work without category label */
        }
        setNonprofitProps({
          ...row,
          nonprofitCategoryName,
        })
      })
      .catch(() => {
        setNonprofitProps(undefined)
      })
  }, [])

  return {
    detailProps,
    loading,
    getNonprofitInfo,
    deliveryType,
    setInStoreDeliveryType,
    nonprofitProps,
  }
}
